/**
 * FibiChat.jsx · v101.24.0 · Chat de «Fibi», la asistente virtual (Ayuda IA).
 * Lo usan el botón flotante (FibiWidget) y la página completa /ayuda.
 * La conversación se comparte entre ambos durante la sesión del navegador.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Send, Loader2, RotateCcw, BookOpen } from "lucide-react";

const STORE_KEY = "afc_fibi_chat";
const WELCOME = {
  role: "assistant",
  content: "¡Hola! Soy **Fibi**, tu asistente virtual de AlfaField. Pregúntame cómo funciona cualquier parte del sistema: órdenes, Kanban, app móvil, inventario, aprovisionamiento o la integración con Odoo.",
};

export function loadFibiHistory() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    return Array.isArray(arr) && arr.length ? arr : [WELCOME];
  } catch {
    return [WELCOME];
  }
}

function saveHistory(msgs) {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(msgs.slice(-40))); } catch { /* ignore */ }
}

/** Render mínimo y seguro: **negrita**, listas con "-", "*" o "1." y saltos de línea. */
function Inline({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") && p.length > 4
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

export function RichText({ text }) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((ln) => {
    const ul = ln.match(/^\s*[-*•]\s+(.*)$/);
    const ol = ln.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (ul || ol) {
      const type = ul ? "ul" : "ol";
      if (!list || list.type !== type) { flush(); list = { type, items: [] }; }
      list.items.push(ul ? ul[1] : ol[2]);
      return;
    }
    flush();
    const h = ln.match(/^#{1,4}\s+(.*)$/);
    if (h) blocks.push({ type: "h", text: h[1] });
    else if (ln.trim()) blocks.push({ type: "p", text: ln });
  });
  flush();
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => {
        if (b.type === "ul") return <ul key={i} className="list-disc pl-5 space-y-0.5">{b.items.map((t, j) => <li key={j}><Inline text={t} /></li>)}</ul>;
        if (b.type === "ol") return <ol key={i} className="list-decimal pl-5 space-y-0.5">{b.items.map((t, j) => <li key={j}><Inline text={t} /></li>)}</ol>;
        if (b.type === "h") return <div key={i} className="font-semibold mt-1"><Inline text={b.text} /></div>;
        return <p key={i}><Inline text={b.text} /></p>;
      })}
    </div>
  );
}

export default function FibiChat({ compact = false, onSuggestions }) {
  const [msgs, setMsgs] = useState(loadFibiHistory);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/v1/help/topics").then((r) => {
      if (!alive) return;
      setSuggested(r.data?.suggested || []);
      onSuggestions && onSuggestions(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [onSuggestions]);

  useEffect(() => { saveHistory(msgs); }, [msgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, busy]);

  const ask = useCallback(async (q) => {
    const question = String(q || "").trim();
    if (!question || busy) return;
    const history = msgs.filter((m) => m !== WELCOME && !m.error).slice(-6).map((m) => ({ role: m.role, content: m.content }));
    setMsgs((prev) => [...prev, { role: "user", content: question }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/v1/help/ask", { question, history });
      setMsgs((prev) => [...prev, { role: "assistant", content: r.data?.answer || "Sin respuesta.", sources: r.data?.sources || [], mode: r.data?.mode }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsgs((prev) => [...prev, { role: "assistant", error: true, content: typeof detail === "string" ? detail : "No pude responder en este momento. Inténtalo de nuevo en unos segundos." }]);
    } finally {
      setBusy(false);
    }
  }, [busy, msgs]);

  // Permite que la página /ayuda dispare preguntas desde la lista de temas
  useEffect(() => {
    const h = (ev) => ask(ev.detail);
    window.addEventListener("fibi-ask", h);
    return () => window.removeEventListener("fibi-ask", h);
  }, [ask]);

  const reset = () => { setMsgs([WELCOME]); };
  const onlyWelcome = msgs.length === 1;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white text-gray-800" data-testid="fibi-chat">
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
              m.role === "user" ? "bg-[#0A3747] text-white rounded-br-sm"
                : m.error ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-sm"
                : "bg-gray-100 rounded-bl-sm"}`}>
              {m.role === "user" ? m.content : <RichText text={m.content} />}
              {m.sources?.length > 0 && (
                <details className="mt-2 text-[11px] text-gray-500">
                  <summary className="cursor-pointer select-none flex items-center gap-1"><BookOpen size={11} /> Fuentes ({m.sources.length})</summary>
                  <ul className="mt-1 space-y-0.5">
                    {m.sources.map((s, j) => <li key={j}>• {s.doc} › {s.section} <span className="text-gray-400">({s.kind})</span></li>)}
                  </ul>
                </details>
              )}
              {m.mode === "sin_ia" && <div className="mt-1 text-[10px] text-amber-700">Respuesta sin IA (búsqueda en la ayuda)</div>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Fibi está pensando…
            </div>
          </div>
        )}
        {onlyWelcome && suggested.length > 0 && (
          <div className="pt-1">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">Puedes preguntarme</div>
            <div className="flex flex-wrap gap-1.5">
              {suggested.slice(0, compact ? 5 : 10).map((s) => (
                <button key={s} onClick={() => ask(s)} className="text-left text-xs px-2.5 py-1.5 rounded-full border border-[#0A3747]/20 text-[#0A3747] hover:bg-[#E0E621]/30 transition" data-testid="fibi-suggestion">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); ask(input); }}
        className="border-t border-gray-200 p-2 flex items-end gap-2 bg-white"
      >
        <button type="button" onClick={reset} title="Nueva conversación" className="p-2 text-gray-400 hover:text-[#0A3747]" data-testid="fibi-reset">
          <RotateCcw size={16} />
        </button>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(input); } }}
          rows={1}
          maxLength={1000}
          placeholder="Escribe tu pregunta…"
          className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3747]/30 max-h-28 bg-white text-gray-800"
          data-testid="fibi-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="p-2.5 rounded-xl bg-[#0A3747] text-[#E0E621] disabled:opacity-40" data-testid="fibi-send">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
